/**
 * SaveToDashboardModal - Select existing dashboard or create new one
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItemButton, ListItemText, TextField, Box, Typography, CircularProgress, Divider } from '@mui/material';
import { Plus } from 'lucide-react';
import {} from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Dashboard {
  id: string;
  name: string;
  description: string | null;
}

interface ChartData {
  label: string;
  value: number;
}

interface SaveToDashboardModalProps {
  open: boolean;
  onClose: () => void;
  chartType: 'bar' | 'line' | 'pie';
  chartData: ChartData[];
  chartTitle: string;
}

export function SaveToDashboardModal({ open, onClose, chartType, chartData, chartTitle }: SaveToDashboardModalProps) {
  const { data: user } = useCurrentUser();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);

  useEffect(() => {
    if (open && user?.tenant_id) {
      fetchDashboards();
    }
  }, [open, user?.tenant_id]);

  const fetchDashboards = async () => {
    if (!user?.tenant_id) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/dashboards?tenant_id=${user.tenant_id}&user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setDashboards(data.dashboards || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.tenant_id || !user?.id) return;
    setSaving(true);

    try {
      let dashboardId = selectedDashboardId;

      // Create new dashboard if needed
      if (showNewForm && newDashboardName.trim()) {
        const createResponse = await fetch(`${API_URL}/dashboards`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: user.tenant_id,
            created_by: user.id,
            name: newDashboardName.trim(),
          }),
        });
        if (!createResponse.ok) throw new Error('Failed to create dashboard');
        const created = await createResponse.json();
        dashboardId = created.dashboard.id;
      }

      if (!dashboardId) return;

      // Save widget to dashboard
      const widgetResponse = await fetch(`${API_URL}/dashboards/${dashboardId}/widgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: user.tenant_id,
          name: chartTitle,
          chart_type: chartType,
          chart_config: { data: chartData },
          query_config: {},
        }),
      });

      if (!widgetResponse.ok) throw new Error('Failed to save widget');

      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const canSave = showNewForm ? newDashboardName.trim().length > 0 : selectedDashboardId !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save to Dashboard</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!showNewForm && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a dashboard or create a new one
                </Typography>
                <List>
                  {dashboards.map((dashboard) => (
                    <ListItemButton key={dashboard.id} selected={selectedDashboardId === dashboard.id} onClick={() => setSelectedDashboardId(dashboard.id)}>
                      <ListItemText primary={dashboard.name} secondary={dashboard.description} />
                    </ListItemButton>
                  ))}
                </List>
                {dashboards.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No dashboards yet
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Button startIcon={<Plus size={16} />} onClick={() => setShowNewForm(true)} fullWidth>
                  Create New Dashboard
                </Button>
              </>
            )}
            {showNewForm && (
              <Box>
                <TextField autoFocus fullWidth label="Dashboard Name" value={newDashboardName} onChange={(e) => setNewDashboardName(e.target.value)} sx={{ mb: 2 }} />
                <Button variant="text" onClick={() => setShowNewForm(false)}>
                  Back to list
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
